/**
 * RhCloturePanel — onglet « Paie & clôture » de la brique RH.
 * État de la période, suivi des clôtures par service, rattrapages reportés,
 * clôture définitive + réouverture exceptionnelle. Conforme mockup_rh_paie_cloture.
 */
import { useState, useEffect } from 'react';
import { Card, Button, Alert, Badge, Field } from '@/components/ui';
import { Dialog } from '@/components/ui/Dialog';
import { api, ApiException } from '@/lib/api';
import { Lock, Unlock, CheckCircle2, Clock } from 'lucide-react';
import { RhStats, RhServicePill } from './rh-ui';
import type { RhPeriodePaie } from '@roronoa-collectivites/shared-types';

const MOIS = ['', 'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];

const STATUT_META: Record<string, { label: string; variant: 'success' | 'urgent' | 'neutral' | 'info'; icon: typeof Lock }> = {
  ouvert: { label: 'Ouverte', variant: 'success', icon: Unlock },
  pre_cloture: { label: 'Pré-clôturée', variant: 'urgent', icon: Clock },
  cloture: { label: 'Clôturée', variant: 'neutral', icon: Lock },
  rouvert: { label: 'Rouverte', variant: 'info', icon: Unlock },
};

export function RhCloturePanel() {
  const now = new Date();
  const [annee, setAnnee] = useState(now.getFullYear());
  const [mois, setMois] = useState(now.getMonth() + 1);
  const [periode, setPeriode] = useState<RhPeriodePaie | null>(null);
  const [reports, setReports] = useState<Array<{ agentNom: string; service: string; typeElement: string; montant: number; libelle: string | null; motif: string | null }>>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [confirmCloture, setConfirmCloture] = useState(false);
  const [reouvrir, setReouvrir] = useState<{ annee: number; mois: number } | null>(null);
  const [motifReouv, setMotifReouv] = useState('');
  const [busy, setBusy] = useState(false);

  async function charger() {
    setLoading(true); setError(null);
    try {
      const [p, r] = await Promise.all([
        api.rh.periode(annee, mois),
        api.rh.reportsPaie(annee, mois).catch(() => []),
      ]);
      setPeriode(p); setReports(r);
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
    finally { setLoading(false); }
  }
  useEffect(() => { void charger(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [annee, mois]);

  async function cloturer() {
    setBusy(true); setError(null); setMsg(null);
    try {
      await api.rh.cloturerDefinitif(annee, mois);
      setMsg('Période clôturée.'); setConfirmCloture(false); await charger();
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
    finally { setBusy(false); }
  }
  async function faireReouverture() {
    if (!reouvrir) return;
    setBusy(true); setError(null); setMsg(null);
    try {
      await api.rh.rouvrirPeriode(reouvrir.annee, reouvrir.mois, motifReouv);
      setMsg('Période rouverte.'); setReouvrir(null); setMotifReouv(''); await charger();
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
    finally { setBusy(false); }
  }

  const meta = periode ? STATUT_META[periode.statut] : null;
  const nbClotures = periode?.services.filter((s) => s.statut === 'cloture').length ?? 0;
  const nbServices = periode?.services.length ?? 0;
  const totalHeures = periode?.services.reduce((s, x) => s + x.totalHeures, 0) ?? 0;

  function prevMois() { if (mois === 1) { setMois(12); setAnnee((a) => a - 1); } else setMois((m) => m - 1); }
  function nextMois() { if (mois === 12) { setMois(1); setAnnee((a) => a + 1); } else setMois((m) => m + 1); }

  return (
    <div className="space-y-5">
      {error && <Alert variant="error">{error}</Alert>}
      {msg && <Alert variant="success">{msg}</Alert>}

      {/* Bandeau état de période */}
      <Card className="p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <button type="button" onClick={prevMois} className="rounded-r-md border border-border px-2 py-1 text-ink-muted hover:text-ink">←</button>
            <div className="min-w-[140px] text-center">
              <div className="text-2xs uppercase tracking-r-eyebrow text-ink-subtle">Période de paie</div>
              <div className="text-lg font-bold text-ink">{MOIS[mois]} {annee}</div>
            </div>
            <button type="button" onClick={nextMois} className="rounded-r-md border border-border px-2 py-1 text-ink-muted hover:text-ink">→</button>
          </div>
          {meta && (
            <div>
              <Badge variant={meta.variant}><meta.icon className="mr-1 inline h-3 w-3" />{meta.label}</Badge>
              {periode?.dateVerrouAuto && periode.statut === 'ouvert' && (
                <div className="mt-1 text-xs text-ink-muted">Verrouillage auto le {new Date(`${periode.dateVerrouAuto}T00:00:00`).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}</div>
              )}
              {periode?.clotureAt && (
                <div className="mt-1 text-xs text-ink-muted">Clôturée le {new Date(periode.clotureAt).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</div>
              )}
            </div>
          )}
          <div className="ml-auto flex gap-2">
            {periode && (periode.statut === 'pre_cloture' || periode.statut === 'ouvert' || periode.statut === 'rouvert') && (
              <Button variant="rh" onClick={() => setConfirmCloture(true)}><Lock className="h-4 w-4" /> Vérifier &amp; clôturer</Button>
            )}
            {periode && periode.statut === 'cloture' && (
              <Button variant="secondary" onClick={() => { setReouvrir({ annee, mois }); setMotifReouv(''); }}><Unlock className="h-4 w-4" /> Rouvrir (exceptionnel)</Button>
            )}
          </div>
        </div>
      </Card>

      {loading ? <p className="text-sm text-ink-muted">Chargement…</p> : (
      <>
      <RhStats items={[
        { label: 'Services clôturés', value: `${nbClotures} / ${nbServices}`, tone: nbClotures === nbServices && nbServices > 0 ? 'ok' : 'default' },
        { label: 'Total heures', value: Math.round(totalHeures) },
        { label: 'Rattrapages reportés', value: reports.length, tone: reports.length > 0 ? 'warn' : 'default' },
      ]} />

      {/* Suivi des clôtures par service */}
      <Card className="overflow-hidden p-0">
        <div className="flex items-center gap-2.5 border-b border-border px-4 py-3">
          <h2 className="font-display text-sm font-semibold text-ink">Clôture par service</h2>
          <span className="ml-auto text-xs text-ink-subtle">{nbClotures} / {nbServices} clôturés</span>
        </div>
        {nbServices === 0 ? (
          <p className="px-4 py-5 text-center text-sm text-ink-subtle">Aucun service n'a encore transmis de données pour cette période.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-surface/40 text-left text-[10px] uppercase tracking-r-eyebrow text-ink-subtle">
                  <th className="px-4 py-2.5 font-medium">Service</th>
                  <th className="px-4 py-2.5 font-medium">État</th>
                  <th className="px-4 py-2.5 font-medium">Clôturé le</th>
                  <th className="px-4 py-2.5 text-right font-medium">Heures</th>
                </tr>
              </thead>
              <tbody>
                {periode!.services.map((s) => (
                  <tr key={s.service} className="border-b border-border/60 last:border-0">
                    <td className="px-4 py-2.5"><RhServicePill service={s.service} /></td>
                    <td className="px-4 py-2.5">
                      {s.statut === 'cloture'
                        ? <Badge variant="success"><CheckCircle2 className="mr-1 inline h-3 w-3" />Clôturé</Badge>
                        : <Badge variant="urgent"><Clock className="mr-1 inline h-3 w-3" />En attente</Badge>}
                    </td>
                    <td className="px-4 py-2.5 text-ink-muted">{s.clotureAt ? new Date(s.clotureAt).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-ink">{Math.round(s.totalHeures)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="px-4 py-2.5 text-xs text-ink-subtle">Les services « en attente » seront inclus automatiquement à la clôture définitive avec leurs données actuelles.</div>
      </Card>

      {/* Rattrapages reportés */}
      {reports.length > 0 && (
        <Card className="overflow-hidden p-0">
          <div className="flex items-center gap-2.5 border-b border-border px-4 py-3">
            <Badge variant="info">↪ {reports.length}</Badge>
            <h2 className="font-display text-sm font-semibold text-ink">Rattrapages reportés sur ce mois</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-surface/40 text-left text-[10px] uppercase tracking-r-eyebrow text-ink-subtle">
                  <th className="px-4 py-2.5 font-medium">Agent</th>
                  <th className="px-4 py-2.5 font-medium">Service</th>
                  <th className="px-4 py-2.5 font-medium">Élément</th>
                  <th className="px-4 py-2.5 font-medium">Motif</th>
                </tr>
              </thead>
              <tbody>
                {reports.map((r, i) => (
                  <tr key={i} className="border-b border-border/60 last:border-0">
                    <td className="px-4 py-2.5 text-ink">{r.agentNom}</td>
                    <td className="px-4 py-2.5"><RhServicePill service={r.service} /></td>
                    <td className="px-4 py-2.5 text-ink-muted">{r.libelle ?? r.typeElement}</td>
                    <td className="px-4 py-2.5 text-ink-subtle">{r.motif ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
      </>
      )}

      {/* Modale clôture définitive */}
      <Dialog open={confirmCloture} onClose={() => setConfirmCloture(false)} title={`Clôturer définitivement — ${MOIS[mois]} ${annee}`}
        footer={<>
          <Button variant="ghost" onClick={() => setConfirmCloture(false)}>Annuler</Button>
          <Button variant="danger" loading={busy} onClick={() => void cloturer()}><Lock className="h-4 w-4" /> Confirmer la clôture</Button>
        </>}>
        <div className="space-y-4">
          <Alert variant="warning">
            <strong>Attention :</strong> une fois clôturée, plus aucune modification ne sera possible sur cette période (hors arrêts maladie / AT). Les services en attente seront inclus automatiquement.
          </Alert>
          <div className="rounded-r-lg border border-border bg-surface/40 p-3 text-sm">
            <div className="flex justify-between py-1"><span className="text-ink-muted">Services clôturés</span><strong className="text-ink">{nbClotures} / {nbServices}</strong></div>
            <div className="flex justify-between py-1"><span className="text-ink-muted">Total heures</span><strong className="text-ink">{Math.round(totalHeures)} h</strong></div>
            <div className="flex justify-between py-1"><span className="text-ink-muted">Rattrapages reportés</span><strong className="text-ink">{reports.length}</strong></div>
          </div>
        </div>
      </Dialog>

      {/* Modale réouverture */}
      <Dialog open={!!reouvrir} onClose={() => setReouvrir(null)} title={`Rouvrir ${reouvrir ? `${MOIS[reouvrir.mois]} ${reouvrir.annee}` : ''} — exceptionnel`}
        footer={<>
          <Button variant="ghost" onClick={() => setReouvrir(null)}>Annuler</Button>
          <Button variant="rh" loading={busy} disabled={motifReouv.trim().length < 3} onClick={() => void faireReouverture()}><Unlock className="h-4 w-4" /> Rouvrir la période</Button>
        </>}>
        <div className="space-y-4">
          <Alert variant="warning">
            La réouverture est <strong>exceptionnelle</strong> et <strong>tracée</strong> (qui, quand, pourquoi). Ex. : oubli d'heures sup validé par le manager, à payer sans attendre la régularisation.
          </Alert>
          <Field label="Motif obligatoire" htmlFor="motif-reouv">
            <input id="motif-reouv" type="text" value={motifReouv} onChange={(e) => setMotifReouv(e.target.value)}
              placeholder="Ex. oubli heures sup agent2 validé par manager2"
              className="h-11 w-full rounded-r-lg border border-border bg-surface/60 px-3.5 text-sm text-ink" />
          </Field>
        </div>
      </Dialog>
    </div>
  );
}
